import React, { useState, useEffect, useCallback, useRef } from "react";

import {
  Share2,
  Link,
  AlertCircle,
  Copy,
  Check,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  LayoutGrid,
  CheckCircle2,
  Loader2,
  Cable,
  ChevronRight,
  Webhook,
  Settings2,
  ShieldCheck,
  Clock,
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../shadcn/ui/dialog";
import { Card, CardContent } from "../../../shadcn/ui/card";
import { Button } from "../../../shadcn/ui/button";
import { Switch } from "../../../shadcn/ui/switch";
import { Label } from "../../../shadcn/ui/label";
import { formatDateTime } from "../../../utils/dateUtils";
import { formatTimeAgo } from "../../../utils/timeAgoUtils";
import HConfirmDialog from "../../../hcomponents/HConfirmDialog";
import { Input } from "../../../shadcn/ui/input";
import { Badge } from "../../../shadcn/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../shadcn/ui/select";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";

import { Empty, EmptyHeader, EmptyTitle, EmptyDescription, EmptyMedia } from "../../../shadcn/ui/empty";
import federationService from "../../../services/host-apiServices/federationService";
import { Constants } from "../../../utils/constants";
import toastUtils from "../../../utils/toast";
import StatusBadge from "./components/StatusBadge";
import BindingFlowDialog from "./components/BindingFlowDialog";
import PeerBaseUrlField, { normalizePeerBaseUrl } from "../../../components/PeerBaseUrlField";

const deriveApiHost = appUrl => {
  try {
    const url = new URL(appUrl);
    url.hostname = url.hostname.replace(/^app\./, "api.");
    return url.origin;
  } catch {
    return null;
  }
};

const fixWebhookUrlToMatchDomain = (webhookUrl, remoteUrl) => {
  if (!webhookUrl || !remoteUrl) return webhookUrl;
  const correctApiHost = deriveApiHost(remoteUrl);
  if (!correctApiHost) return webhookUrl;
  try {
    const parsed = new URL(webhookUrl);
    const target = new URL(correctApiHost);
    parsed.protocol = target.protocol;
    parsed.hostname = target.hostname;
    parsed.port = target.port;
    return parsed.toString();
  } catch {
    return webhookUrl;
  }
};


const fieldTypeToInput = (field, value, onChange) => {
  const id = `field-${field.key}`;

  if (field.key === "remoteBaseUrl") {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <PeerBaseUrlField value={normalizePeerBaseUrl(value)} onChange={v => onChange(field.key, v)} />
        <p className="text-xs text-muted-foreground">Choose which Spacia environment the peer account runs on.</p>
      </div>
    );
  }

  if (field.type === "BOOL") {
    return (
      <div className="flex items-center gap-2">
        <Switch id={id} checked={value === true} onCheckedChange={v => onChange(field.key, v)} />
        <Label htmlFor={id}>{field.label}</Label>
      </div>
    );
  }

  if (field.type === "ENUM" && field.options?.length) {
    return (
      <div className="space-y-1.5">
        <Label htmlFor={id}>
          {field.label}
          {field.required && <span className="text-red-500 ml-0.5">*</span>}
        </Label>
        <Select value={value || ""} onValueChange={v => onChange(field.key, v)}>
          <SelectTrigger id={id}>
            <SelectValue placeholder={`Select ${field.label}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map(opt => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  const inputType = field.secret ? "password" : field.type === "INT" ? "number" : field.type === "URL" ? "url" : "text";

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.required && <span className="text-red-500 ml-0.5">*</span>}
      </Label>
      <Input
        id={id}
        type={inputType}
        value={value || ""}
        onChange={e => {
          const v = field.type === "INT" ? Number(e.target.value) : e.target.value;
          onChange(field.key, v);
        }}
        placeholder={field.label}
      />
    </div>
  );
};

const generateSigningSecret = () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let raw = "";
  for (let i = 0; i < bytes.length; i++) raw += String.fromCharCode(bytes[i]);
  return btoa(raw).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

// ─── TestActivateFlow ─────────────────────────────────────────────────────────
// Shown after a binding is created. Guides the user through Test → Activate
// without any bullet-point instructions. EXPOSE adds a "waiting for partner" step.
const TestActivateFlow = ({ testState, testError, activating, activated, onTest, onActivate, onDone, onNext, direction }) => {
  const isExpose = direction === "EXPOSE";

  // Step indices: 0=share(expose only), 1=test, 2=activate
  const currentStep = activated ? 3 : testState === "passed" ? 2 : testState !== "idle" ? 1 : isExpose ? 0 : 1;

  const StepDot = ({ index, label }) => {
    const done = currentStep > index;
    const active = currentStep === index;
    return (
      <div className="flex items-center gap-2">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-all ${
          done ? "bg-green-500 text-white" :
          active ? "bg-indigo-600 text-white ring-4 ring-indigo-100" :
          "bg-gray-100 text-gray-400"
        }`}>
          {done ? <Check className="w-3 h-3" /> : index + 1}
        </div>
        <span className={`text-sm font-medium ${active ? "text-gray-900" : done ? "text-green-700" : "text-gray-400"}`}>
          {label}
        </span>
      </div>
    );
  };

  return (
    <div className="space-y-4 pt-1">
      {/* Progress steps */}
      <div className={`flex items-center gap-0 ${isExpose ? "justify-between" : "justify-center"}`}>
        {isExpose && <StepDot index={0} label="Share key" />}
        <div className={`flex-1 h-px mx-3 ${isExpose ? "block" : "hidden"} ${currentStep > 0 ? "bg-green-400" : "bg-gray-200"}`} />
        <StepDot index={isExpose ? 1 : 0} label="Test connection" />
        <div className={`flex-1 h-px mx-3 ${testState === "passed" || activated ? "bg-green-400" : "bg-gray-200"}`} />
        <StepDot index={isExpose ? 2 : 1} label="Activate" />
      </div>

      {/* Action area — changes with each step */}
      {!activated && (
        <div className="rounded-xl border border-gray-100 bg-gray-50 p-4 space-y-3">

          {/* EXPOSE: share step — waiting for partner */}
          {isExpose && testState === "idle" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                  <RefreshCw className="w-4 h-4 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Waiting for your partner</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Send them the key above. Once they set up their side, test the connection.
                  </p>
                </div>
              </div>
              <Button onClick={onTest} className="w-full" variant="outline">
                <Play className="w-4 h-4 mr-2" />
                Test connection anyway
              </Button>
            </div>
          )}

          {/* CONSUME: test immediately */}
          {!isExpose && testState === "idle" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
                  <Play className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">Ready to test</p>
                  <p className="text-xs text-gray-500 mt-0.5">Verify the connection is live before activating.</p>
                </div>
              </div>
              <Button onClick={onTest} className="w-full">
                <Play className="w-4 h-4 mr-2" />
                Test connection
              </Button>
            </div>
          )}

          {/* Running */}
          {testState === "running" && (
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 animate-spin text-indigo-600 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-gray-900">Testing…</p>
                <p className="text-xs text-gray-500">Pinging the connection</p>
              </div>
            </div>
          )}

          {/* Failed */}
          {testState === "failed" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center shrink-0">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-700">Test failed</p>
                  <p className="text-xs text-red-500 mt-0.5">{testError || "Check credentials and try again."}</p>
                </div>
              </div>
              <Button onClick={onTest} variant="outline" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry test
              </Button>
            </div>
          )}

          {/* Passed → show activate */}
          {testState === "passed" && (
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-800">Connection verified</p>
                  <p className="text-xs text-gray-500 mt-0.5">Go live by activating the integration.</p>
                </div>
              </div>
              <Button
                onClick={onActivate}
                disabled={activating}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {activating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                {activating ? "Activating…" : "Activate integration"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Activated success + next step */}
      {activated && (
        <div className="space-y-3">
          <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-green-500 flex items-center justify-center shrink-0">
              <CheckCircle2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-bold text-green-900">Integration is live</p>
              <p className="text-xs text-green-700 mt-0.5">
                {isExpose
                  ? "Your spaces are now shared. Your partner should activate on their end too."
                  : "You're connected. Events will flow once the host activates their side too."}
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-indigo-100 bg-indigo-50/60 p-4 space-y-3">
            <p className="text-sm font-semibold text-gray-900">What's next</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {isExpose
                ? "Keep your access key handy — your partner needs it to connect. You can manage keys and webhooks anytime."
                : "We've started pulling your partner's spaces. Review, edit, and approve what appears in your inventory."}
            </p>
            <Button onClick={onNext} className="w-full">
              {isExpose ? "Manage integration" : "Review imported spaces"}
            </Button>
          </div>
        </div>
      )}

      {/* Footer action */}
      <div className="flex justify-end pt-1">
        <Button variant={activated ? "default" : "ghost"} onClick={onDone}>
          {activated ? "Done" : "Close for now"}
        </Button>
      </div>
    </div>
  );
};

const ConnectDialog = ({ provider, direction, open, onOpenChange, onConnected, onNext }) => {
  const [step, setStep] = useState("form");
  const [formValues, setFormValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [webhookInfo, setWebhookInfo] = useState(null);
  const [createdBindingId, setCreatedBindingId] = useState(null);
  // test / activate flow
  const [testState, setTestState] = useState("idle"); // idle | running | passed | failed
  const [testError, setTestError] = useState(null);
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep("form");
      setFormValues({});
      setApiKey(null);
      setApiKeyCopied(false);
      setWebhookInfo(null);
      setCreatedBindingId(null);
      setTestState("idle");
      setTestError(null);
      setActivating(false);
      setActivated(false);
    }
  }, [open]);

  const handleTest = async () => {
    if (!createdBindingId) return;
    setTestState("running");
    setTestError(null);
    try {
      await federationService.testBinding(createdBindingId);
      setTestState("passed");
    } catch (err) {
      setTestState("failed");
      setTestError(err?.response?.data?.message || "Test failed — check credentials and try again");
    }
  };

  const handleActivate = async () => {
    if (!createdBindingId) return;
    setActivating(true);
    try {
      await federationService.activateBinding(createdBindingId);
      setActivated(true);
    } catch (err) {
      toastUtils.error(err?.response?.data?.message || "Activation failed");
    } finally {
      setActivating(false);
    }
  };

  useEffect(() => {
    if (provider) {
      const defaults = {};
      provider.configFields.forEach(f => {
        if (f.type === "BOOL") defaults[f.key] = false;
      });
      setFormValues(defaults);
    }
  }, [provider]);

  const handleFieldChange = useCallback((key, value) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = async () => {
    if (!provider) return;
    setSubmitting(true);

    try {
      const config = {};
      const secrets = {};

      provider.configFields.forEach(f => {
        const val = formValues[f.key];
        if (f.secret) {
          secrets[f.key] = String(val ?? "");
        } else {
          config[f.key] = String(val ?? "");
        }
      });

      const binding = await federationService.createBinding({
        providerCode: provider.code,
        direction,
        scopeType: "ORG",
        scopeId: null,
        capabilitiesEnabled: provider.capabilities,
        config,
      });

      const id = binding.id || binding.bindingId;
      setCreatedBindingId(id);

      if (direction === "EXPOSE") {
        const keyResp = await federationService.mintApiKey(id, {
          capabilities: [],
          label: `consumer-${provider.code}`,
          expiresAt: "2027-06-01T00:00:00Z",
        });
        setApiKey(keyResp.key);

        await federationService.setCredentials(id, {
          secrets: { hostApiKey: keyResp.key },
        });

        setStep("api-key");
      } else {
        const hostApiKey = secrets.hostApiKey;
        const signingSecret = generateSigningSecret();

        const allSecrets = { ...secrets, webhookSigningSecret: signingSecret };
        await federationService.setCredentials(id, { secrets: allSecrets });

        const wh = await federationService.getWebhookInfo(id);

        const baseUrl = Constants.INTEGRATIONS_API_BASE_URL.replace("/api/v2/integrations", "");
        const constructedUrl = `${baseUrl}/integrations/webhooks/${provider.code}/${wh.token}`;
        const webhookUrl = config.remoteBaseUrl
          ? fixWebhookUrlToMatchDomain(constructedUrl, config.remoteBaseUrl)
          : constructedUrl;

        if (hostApiKey) {
          await federationService.registerWebhookOnHost(config.remoteBaseUrl || baseUrl, hostApiKey, {
            webhookUrl,
            signingSecret,
            eventTypes: ["inventory.changed", "property.changed", "booking.changed", "booking.canceled"],
          });
        }

        setWebhookInfo({ ...wh, inboundUrl: constructedUrl });
        setStep("webhook");
      }
    } catch (err) {
      logger.error("Failed to connect provider:", err);
      showApiMessage(err, "connect integration");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async text => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // fallback
    }
  };

  const handleDone = () => {
    onConnected();
    onOpenChange(false);
  };

  const handleNext = () => {
    onConnected();
    if (onNext && createdBindingId) onNext(createdBindingId);
    else onOpenChange(false);
  };

  if (!provider) return null;

  const nonSecretFields = provider.configFields.filter(f => !f.secret);
  const secretFields = provider.configFields.filter(
    f => f.secret && !(direction === "EXPOSE" && f.key === "hostApiKey")
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] rounded-2xl max-h-[90vh] overflow-y-auto">
        {step === "form" && (
          <>
            <DialogHeader>
              <DialogTitle>
                {direction === "EXPOSE" ? "Share" : "Connect"} {provider.code}
              </DialogTitle>
              <DialogDescription>
                {provider.specVersion && (
                  <span className="block text-xs text-muted-foreground">v{provider.specVersion}</span>
                )}
              </DialogDescription>
            </DialogHeader>

            <div className="flex items-center gap-2 mb-2">
              <Badge variant="secondary" className="bg-indigo-100 text-indigo-700 text-xs">
                {direction === "EXPOSE" ? "Sharing" : "Connecting"}
              </Badge>
            </div>

            {provider.capabilities?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {provider.capabilities.map(cap => (
                  <Badge key={cap} variant="secondary" className="text-xs bg-slate-100 text-slate-700">
                    {cap}
                  </Badge>
                ))}
              </div>
            )}

            <div className="space-y-4 py-2">
              {nonSecretFields.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Configuration</h4>
                  {nonSecretFields.map(f => (
                    <div key={f.key}>{fieldTypeToInput(f, formValues[f.key], handleFieldChange)}</div>
                  ))}
                </div>
              )}

              {secretFields.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Credentials</h4>
                  {secretFields.map(f => (
                    <div key={f.key}>{fieldTypeToInput(f, formValues[f.key], handleFieldChange)}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={submitting}>
                {submitting && <Loader2 className="mr-2 w-4 h-4 animate-spin" />}
                {direction === "EXPOSE" ? "Share" : "Connect"}
              </Button>
            </div>
          </>
        )}

        {step === "api-key" && apiKey && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Ready to share
              </DialogTitle>
              <DialogDescription>
                Send this key to the CONSUME partner. They paste it in their integration and click "Connect live updates".
              </DialogDescription>
            </DialogHeader>

            {/* API Key copy block */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                API key — give this to the CONSUME partner
              </p>
              <div className="flex gap-2">
                <Input value={apiKey} readOnly className="bg-white font-mono text-xs" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    handleCopy(apiKey);
                    setApiKeyCopied(true);
                  }}
                >
                  {apiKeyCopied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              {apiKeyCopied && (
                <p className="text-xs text-green-700 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> Copied to clipboard
                </p>
              )}
            </div>

            {/* Inline test → activate flow */}
            <TestActivateFlow
              testState={testState}
              testError={testError}
              activating={activating}
              activated={activated}
              onTest={handleTest}
              onActivate={handleActivate}
              onDone={handleDone}
              onNext={handleNext}
              direction={direction}
            />
          </>
        )}

        {step === "webhook" && webhookInfo && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                Integration created
              </DialogTitle>
              <DialogDescription>
                Next: paste the EXPOSE partner's API key and click "Connect live updates" to finish setup.
              </DialogDescription>
            </DialogHeader>

            {/* Webhook URL */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-2">
              <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide">Your webhook URL</p>
              <div className="flex gap-2">
                <Input value={webhookInfo.inboundUrl} readOnly className="bg-white font-mono text-xs" />
                <Button variant="outline" size="icon" onClick={() => handleCopy(webhookInfo.inboundUrl)}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-blue-600">
                Events will be delivered here. Click "Connect live updates" in the Keys &amp; Webhooks tab to register
                this URL on the EXPOSE partner's server.
              </p>
            </div>

            {/* Inline test → activate flow */}
            <TestActivateFlow
              testState={testState}
              testError={testError}
              activating={activating}
              activated={activated}
              onTest={handleTest}
              onActivate={handleActivate}
              onDone={handleDone}
              onNext={handleNext}
              direction={direction}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Human-readable labels for each capability code
const CAPABILITY_LABELS = {
  INVENTORY_FETCH: { label: "Fetch Inventory", color: "bg-blue-50 text-blue-700 border-blue-200" },
  INVENTORY_CHANGE_RECEIVE: { label: "Receive Inventory Updates", color: "bg-sky-50 text-sky-700 border-sky-200" },
  INVENTORY_PUBLISH: { label: "Publish Inventory", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  AVAILABILITY_CHECK: { label: "Check Availability", color: "bg-teal-50 text-teal-700 border-teal-200" },
  OUTBOUND_BOOKING: { label: "Send Bookings", color: "bg-green-50 text-green-700 border-green-200" },
  EXTERNAL_BOOKING_RECEIVE: { label: "Receive Bookings", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PROPERTY_CONTENT_FETCH: { label: "Fetch Property Content", color: "bg-violet-50 text-violet-700 border-violet-200" },
  PROPERTY_PUBLISH: { label: "Publish Properties", color: "bg-purple-50 text-purple-700 border-purple-200" },
  POST_BOOKING_ENRICHMENT: { label: "Post-Booking Enrichment", color: "bg-amber-50 text-amber-700 border-amber-200" },
};

const WEBHOOK_INFO = {
  NONE: { label: "No webhook", color: "bg-gray-100 text-gray-500" },
  SPACIA_GENERATED: { label: "Webhook: auto-generated", color: "bg-teal-100 text-teal-700" },
  VENDOR_SUPPLIED: { label: "Webhook: vendor-supplied", color: "bg-orange-100 text-orange-700" },
};

const ProviderPickerDialog = ({ open, onOpenChange, direction, providers, onSelect }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="sm:max-w-[560px] rounded-2xl">
      <DialogHeader>
        <DialogTitle>{direction === "EXPOSE" ? "Share Spaces" : "Connect Spaces"}</DialogTitle>
        <DialogDescription>
          {direction === "EXPOSE" ? "Choose a platform to share your spaces with" : "Choose a platform to connect to"}
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto pr-1">
        {providers.length === 0 ? (
          <div className="text-center py-10 text-gray-500">
            <Cable className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p className="font-medium">No platforms available</p>
          </div>
        ) : (
          providers.map(p => {
            const webhookInfo = WEBHOOK_INFO[p.webhookSecretMode] || WEBHOOK_INFO.NONE;
            const secretCount = p.configFields?.filter(f => f.secret).length || 0;
            const configCount = p.configFields?.filter(f => !f.secret).length || 0;
            const isSpacia = (p.code || "").toLowerCase() === "spacia";

            return (
              <button
                key={p.code}
                type="button"
                onClick={() => onSelect(p)}
                className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-indigo-400 hover:bg-indigo-50/40 transition-all group"
              >
                {/* Header row */}
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 rounded-lg bg-slate-100 group-hover:bg-indigo-100 transition-colors shrink-0">
                      {isSpacia ? (
                        <img
                          src="/assets/img/new-logo.svg"
                          alt="Spacia"
                          className="w-6 h-6 object-contain"
                          onError={e => (e.target.style.display = "none")}
                        />
                      ) : (
                        <Cable className="w-5 h-5 text-slate-500 group-hover:text-indigo-600 transition-colors" />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 capitalize">{p.code.replace(/-/g, " ")}</p>
                      {p.specVersion && <p className="text-xs text-gray-400 mt-0.5">{p.specVersion}</p>}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-indigo-500 transition-colors mt-1 shrink-0" />
                </div>

                {/* Capabilities */}
                {p.capabilities?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {p.capabilities.map(cap => {
                      const meta = CAPABILITY_LABELS[cap];
                      return (
                        <span
                          key={cap}
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                            meta?.color || "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          {meta?.label || cap}
                        </span>
                      );
                    })}
                  </div>
                )}

                {/* Footer row: webhook + field counts */}
                <div className="flex items-center gap-3 flex-wrap">
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${webhookInfo.color}`}
                  >
                    <Webhook className="w-3 h-3" />
                    {webhookInfo.label}
                  </span>
                  {configCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">
                      <Settings2 className="w-3 h-3" />
                      {configCount} config field{configCount !== 1 ? "s" : ""}
                    </span>
                  )}
                  {secretCount > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-rose-50 text-rose-600">
                      <ShieldCheck className="w-3 h-3" />
                      {secretCount} credential{secretCount !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </DialogContent>
  </Dialog>
);

const DIRECTION_FILTERS = [
  { value: "", label: "All" },
  { value: "EXPOSE", label: "Share" },
  { value: "CONSUME", label: "Connect" },
];

const IntegrationsSettings = ({ showExposeModal, showConsumeModal, setShowExposeModal, setShowConsumeModal }) => {
  const [bindings, setBindings] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [providerReady, setProviderReady] = useState(true);
  const [dialogBindingId, setDialogBindingId] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null); // { id, name }
  const [testing, setTesting] = useState(null);
  const [activating, setActivating] = useState(null);
  const [pausing, setPausing] = useState(null);
  const [directionFilter, setDirectionFilter] = useState("");
  const directionRef = useRef({});

  // New dynamic onboarding state
  const [pickerDirection, setPickerDirection] = useState(null);
  const [connectProvider, setConnectProvider] = useState(null);
  const [connectDirection, setConnectDirection] = useState("CONSUME");

  const fetchBindings = useCallback(async () => {
    try {
      const data = await federationService.listBindings(directionFilter || undefined);
      const merged = (Array.isArray(data) ? data : []).map(b => ({
        ...b,
        direction: b.direction || directionRef.current[b.id],
      }));
      setBindings(merged);
    } catch {
      setBindings([]);
    } finally {
      setLoading(false);
    }
  }, [directionFilter]);

  const checkProvider = useCallback(async () => {
    try {
      const data = await federationService.getProviders();
      const list = Array.isArray(data) ? data : [];
      setProviders(list);
      const hasSpacia = list.some(p => (p.code || p.providerCode) === "spacia");
      setProviderReady(hasSpacia);
    } catch {
      setProviderReady(false);
    }
  }, []);

  useEffect(() => {
    fetchBindings();
    checkProvider();
  }, [fetchBindings, checkProvider]);

  // When parent opens the modal via header buttons, intercept and open picker instead
  useEffect(() => {
    if (showExposeModal) {
      setPickerDirection("EXPOSE");
      setShowExposeModal(false);
    }
  }, [showExposeModal, setShowExposeModal]);

  useEffect(() => {
    if (showConsumeModal) {
      setPickerDirection("CONSUME");
      setShowConsumeModal(false);
    }
  }, [showConsumeModal, setShowConsumeModal]);

  const handlePickerSelect = useCallback(
    provider => {
      setConnectProvider(provider);
      setConnectDirection(pickerDirection);
      setPickerDirection(null);
    },
    [pickerDirection]
  );

  const handleConnected = useCallback(() => {
    fetchBindings();
  }, [fetchBindings]);

  const typeIcon = dir =>
    dir === "EXPOSE" ? <Share2 className="w-4 h-4 text-purple-600" /> : <Link className="w-4 h-4 text-blue-600" />;

  const typeLabel = dir => (dir === "EXPOSE" ? "Share" : "Connected");

  return (
    <>
      <h2 className="mx-3 mb-4 text-xl font-semibold text-gray-800">Connected Platforms</h2>

      <div className="p-6 mx-3 mb-6 bg-white rounded-2xl border border-gray-100 shadow-sm space-y-5">
        {loading ? (
          <div className="space-y-3">
            {[1, 2].map(i => (
              <div key={i} className="flex items-center gap-4 p-4 border border-slate-50 rounded-2xl animate-pulse">
                <div className="w-11 h-11 bg-slate-100 rounded-xl shrink-0" />
                <div className="flex-1 space-y-2 min-w-0">
                  <div className="h-4 w-32 bg-slate-100 rounded" />
                  <div className="h-3 w-full max-w-[240px] bg-slate-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {!providerReady && (
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium">Federation not ready</p>
                    <p className="mt-1">
                      Ask your admin to set up the federation pepper and run the database migration before creating
                      integrations.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg">
            {DIRECTION_FILTERS.map(f => (
              <button
                key={f.value}
                type="button"
                onClick={() => {
                  setDirectionFilter(f.value);
                  setLoading(true);
                }}
                className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  directionFilter === f.value
                    ? "bg-white text-slate-800 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <p className="text-xs font-semibold text-slate-400">
            {bindings.length} Connection{bindings.length !== 1 ? "s" : ""}
          </p>
        </div>

      {!bindings.length ? (
        <Empty className="py-16">
          <EmptyMedia variant="icon">
            <LayoutGrid className="w-5 h-5" />
          </EmptyMedia>
          <EmptyHeader>
            <EmptyTitle>
              {directionFilter
                ? `No ${DIRECTION_FILTERS.find(f => f.value === directionFilter)?.label || ""} integrations`
                : "No integrations yet"}
            </EmptyTitle>
            <EmptyDescription>
              {directionFilter
                ? "Try a different filter or create a new integration"
                : "Share spaces or connect to another account to get started"}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <div className="space-y-3">
          {bindings.map(b => (
            <div
              key={b.id}
              className="group relative overflow-hidden bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200"
            >
              <div className="flex items-center justify-between p-4 gap-4">
                <button
                  type="button"
                  onClick={() => setDialogBindingId(b.id)}
                  className="flex-1 flex items-center gap-4 text-left min-w-0"
                >
                  {/* Frosted Direction Icon */}
                  <div className={`p-3 rounded-xl shrink-0 transition-transform group-hover:scale-105 ${
                    b.direction === "EXPOSE" 
                      ? "bg-purple-50 text-purple-600 border border-purple-100" 
                      : "bg-blue-50 text-blue-600 border border-blue-100"
                  }`}>
                    {typeIcon(b.direction)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2">
                      <span className="text-sm font-bold text-slate-800 truncate">
                        {b.providerCode ? `${typeLabel(b.direction)} — ${b.providerCode}` : typeLabel(b.direction)}
                      </span>
                      
                      <StatusBadge status={b.status} />

                      {!b.credentialsSet && (b.status === "PENDING" || b.status === "ACTIVATING") && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-ping" />
                          Setup required
                        </span>
                      )}
                    </div>

                    <p className="text-xs text-slate-400 mt-1 font-medium flex items-center gap-1.5">
                      {b.createdAt && (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Created:
                            <span
                              className="font-semibold text-slate-600"
                              title={`${formatDateTime(new Date(b.createdAt)).date} · ${formatDateTime(new Date(b.createdAt)).time}`}
                            >
                              {formatTimeAgo(new Date(b.createdAt))}
                            </span>
                          </span>
                          <span className="text-slate-200">|</span>
                        </>
                      )}
                      <span>Scope:</span>
                      <span className="font-semibold text-slate-600">
                        {b.scopeType === "ORG" ? "All Spaces & Properties" : `Specific Space ID: ${b.scopeId}`}
                      </span>
                      {b.capabilitiesEnabled && (
                        <>
                          <span className="text-slate-200">|</span>
                          <span className="text-slate-500 font-semibold bg-slate-50 border border-slate-100 px-1.5 py-0.25 rounded-md">
                            {b.capabilitiesEnabled.split(",").length} Permission{b.capabilitiesEnabled.split(",").length !== 1 ? "s" : ""}
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                </button>

                {/* Operations Toolbar */}
                <div className="flex items-center gap-1 border-l border-slate-100 pl-3">
                  {b.status === "ACTIVE" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        setPausing(b.id);
                        try {
                          await federationService.pauseBinding(b.id);
                          fetchBindings();
                          toastUtils.success("Integration paused");
                        } catch {
                          toastUtils.error("Failed to pause");
                        } finally {
                          setPausing(null);
                        }
                      }}
                      disabled={pausing === b.id}
                      className="w-8 h-8 rounded-lg text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                      title="Pause Integration"
                    >
                      <Pause className={`w-3.5 h-3.5 ${pausing === b.id ? "animate-pulse" : ""}`} />
                    </Button>
                  )}
                  {(b.status === "PENDING" || b.status === "PAUSED") && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        setActivating(b.id);
                        try {
                          await federationService.activateBinding(b.id);
                          fetchBindings();
                          toastUtils.success("Integration activated");
                        } catch {
                          toastUtils.error("Failed to activate");
                        } finally {
                          setActivating(null);
                        }
                      }}
                      disabled={activating === b.id || !b.lastHealthCheckAt}
                      className="w-8 h-8 rounded-lg text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                      title={b.lastHealthCheckAt ? "Activate Integration" : "Run a test first"}
                    >
                      <Play className={`w-3.5 h-3.5 ${activating === b.id ? "animate-pulse" : ""}`} />
                    </Button>
                  )}
                  {b.status !== "ARCHIVED" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        setTesting(b.id);
                        try {
                          await federationService.testBinding(b.id);
                          toastUtils.success("Healthy");
                        } catch {
                          toastUtils.error("Failed test");
                        } finally {
                          setTesting(null);
                        }
                      }}
                      disabled={testing === b.id}
                      className="w-8 h-8 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                      title="Test Health Check"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${testing === b.id ? "animate-spin" : ""}`} />
                    </Button>
                  )}
                  {b.status !== "ARCHIVED" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => setRemoveTarget({ id: b.id, name: b.providerCode || b.id })}
                      disabled={archiving === b.id}
                      className="w-8 h-8 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50"
                      title="Disconnect Integration"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
          </>
        )}
      </div>

      <ProviderPickerDialog
        open={!!pickerDirection}
        onOpenChange={() => setPickerDirection(null)}
        direction={pickerDirection}
        providers={providers}
        onSelect={handlePickerSelect}
      />

      <ConnectDialog
        provider={connectProvider}
        direction={connectDirection}
        open={!!connectProvider}
        onOpenChange={() => setConnectProvider(null)}
        onConnected={handleConnected}
        onNext={id => {
          setConnectProvider(null);
          setDialogBindingId(id);
        }}
      />

      <BindingFlowDialog
        bindingId={dialogBindingId}
        open={!!dialogBindingId}
        knownDirection={directionRef.current[dialogBindingId]}
        onClose={() => {
          setDialogBindingId(null);
          fetchBindings();
        }}
      />

      <HConfirmDialog
        show={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={async () => {
          if (!removeTarget) return;
          setArchiving(removeTarget.id);
          setRemoveTarget(null);
          try {
            await federationService.archiveBinding(removeTarget.id);
            fetchBindings();
          } catch {
            toastUtils.error("Failed to remove integration");
          } finally {
            setArchiving(null);
          }
        }}
        isLoading={!!archiving}
        title={`Remove "${removeTarget?.name}"?`}
        message="This will disconnect the integration and stop all live updates. You can reconnect it again later if needed."
        confirmText="Remove"
        cancelText="Cancel"
        confirmVariant="destructive"
      />
    </>
  );
};

export default IntegrationsSettings;
